import React, { useState, useMemo } from "react";
import { FreeTrialFormData, AgeGroup, Gender } from "../model/free-trial-types";

interface FreeTrialFormProps {
  onSubmit: (data: FreeTrialFormData) => Promise<void>;
  isLoading?: boolean;
}

interface AgeInfo {
  koreanAge: number;
  actualAge: number;
  birthMonth: number;
}

/**
 * 생년월일로부터 한국 나이, 만나이, 개월수를 계산
 */
function calculateAge(birthDateString: string): AgeInfo | null {
  if (!birthDateString) return null;

  const birthDate = new Date(birthDateString);
  const today = new Date();

  // 유효한 날짜 확인
  if (isNaN(birthDate.getTime())) return null;

  // 만 나이 계산
  let actualAge = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    actualAge--;
  }

  // 한국 나이 계산 (태어난 해를 1세로 함)
  const koreanAge = actualAge + 1;

  // 개월수 계산 (총 개월 수)
  let totalMonths = (today.getFullYear() - birthDate.getFullYear()) * 12;
  totalMonths += today.getMonth() - birthDate.getMonth();
  
  // 일(day)을 고려한 정밀한 개월수
  if (today.getDate() < birthDate.getDate()) {
    totalMonths--;
  }

  return {
    koreanAge,
    actualAge,
    birthMonth: Math.max(0, totalMonths),
  };
}

/**
 * FreeTrialForm - 무료체험 신청 폼 컴포넌트
 * 아이 정보 + 학부모 정보를 함께 입력받습니다
 * 모바일 반응형 지원
 */
export function FreeTrialForm({ onSubmit, isLoading = false }: FreeTrialFormProps) {
  const [formData, setFormData] = useState<FreeTrialFormData>({
    child: {
      name: "",
      age_group: "4",
      birth_month: 0,
      birth_date: "",
    },
    parent: {
      name: "",
      gender: undefined,
      birth_date: "",
      user_id: "",
      password: "",
      password_confirm: "",
      phone_number: "",
      email: "",
      email_verification_code: "",
      is_email_verified: false,
    },
    agree_to_terms: false,
    agree_to_privacy: false,
    agree_to_marketing: false,
    agree_to_parental_consent: false,
    agree_to_all: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailSent, setEmailSent] = useState(false);

  // 아이 생년월일로부터 자동 계산된 나이 정보
  const childAgeInfo = useMemo(() => {
    return calculateAge(formData.child.birth_date);
  }, [formData.child.birth_date]);

  const handleChange = (
    section: "child" | "parent" | "agreements",
    field: string,
    value: any
  ) => {
    if (section === "agreements") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        agree_to_all:
          field === "agree_to_all"
            ? value
            : value &&
              prev.agree_to_privacy &&
              prev.agree_to_terms &&
              prev.agree_to_parental_consent,
      }));
    } else {
      // 아이 생년월일 변경 시 birth_month 자동 업데이트
      if (section === "child" && field === "birth_date") {
        const ageInfo = calculateAge(value);
        setFormData((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
            birth_month: ageInfo?.birthMonth || 0,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
          },
        }));
      }
    }
    if (errors[`${section}.${field}`]) {
      setErrors((prev) => ({
        ...prev,
        [`${section}.${field}`]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.child.name.trim()) {
      newErrors["child.name"] = "아이 이름을 입력해주세요.";
    }
    if (!formData.child.birth_date) {
      newErrors["child.birth_date"] = "아이 생년월일을 입력해주세요.";
    }
    if (!formData.parent.name.trim()) {
      newErrors["parent.name"] = "이름을 입력해주세요.";
    }
    if (!formData.parent.user_id.trim()) {
      newErrors["parent.user_id"] = "아이디를 입력해주세요.";
    }
    if (!formData.parent.password) {
      newErrors["parent.password"] = "비밀번호를 입력해주세요.";
    }
    if (formData.parent.password !== formData.parent.password_confirm) {
      newErrors["parent.password_confirm"] = "비밀번호가 일치하지 않습니다.";
    }
    if (!formData.parent.phone_number.trim()) {
      newErrors["parent.phone_number"] = "휴대전화 번호를 입력해주세요.";
    }
    if (!formData.parent.is_email_verified) {
      newErrors["parent.email"] = "이메일 인증이 필요합니다.";
    }
    if (!formData.agree_to_terms) {
      newErrors["agree_to_terms"] = "이용약관에 동의해주세요.";
    }
    if (!formData.agree_to_privacy) {
      newErrors["agree_to_privacy"] = "개인정보 수집에 동의해주세요.";
    }
    if (!formData.agree_to_parental_consent) {
      newErrors["agree_to_parental_consent"] = "법정대리인 동의가 필요합니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    await onSubmit(formData);
  };

  const handleAllAgree = () => {
    const newAllAgree = !formData.agree_to_all;
    setFormData((prev) => ({
      ...prev,
      agree_to_all: newAllAgree,
      agree_to_terms: newAllAgree,
      agree_to_privacy: newAllAgree,
      agree_to_marketing: newAllAgree,
      agree_to_parental_consent: newAllAgree,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-6xl mx-auto py-6 md:py-12 px-4 md:px-6">
      <div className="mb-10 md:mb-16">
        {/* 제목 */}
        <div className="text-center mb-8 md:mb-12">
          <p className="text-gray-500 text-sm md:text-lg mb-2">무료체험 신청</p>
          <h1 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">AI 맞춤 학습 7일 무료 체험</h1>
          <p className="text-gray-700 text-sm md:text-lg leading-6 md:leading-7">
            전문가가 설계한 맞춤형 콘텐츠를 무료로 체험하고<br />
            우리 아이에게 꼭 맞는 성장 솔루션을 만나보세요.
          </p>
        </div>

        {/* 아이 정보 입력 */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-lg md:text-xl font-medium text-orange-500 mb-4 md:mb-6">우리 아이 정보 입력</h2>

          {/* 아이 이름 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">아이 이름</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="text"
                placeholder="아이 이름을 입력해 주세요."
                value={formData.child.name}
                onChange={(e) => handleChange("child", "name", e.target.value)}
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
              />
            </div>
            {errors["child.name"] && (
              <p className="text-red-500 text-sm mt-2">{errors["child.name"]}</p>
            )}
          </div>

          {/* 아이 나이
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0 md:pt-3.5">
                <span className="text-black text-base md:text-lg font-normal">아이 나이</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <div className="flex gap-2 md:gap-3 flex-wrap">
                {(["4", "5", "6", "7"] as AgeGroup[]).map((age) => (
                  <button
                    key={age}
                    type="button"
                    onClick={() => handleChange("child", "age_group", age)}
                    className={`flex-1 md:flex-none w-24 md:w-40 h-12 md:h-14 rounded-lg text-sm md:text-base font-normal transition-colors ${
                      formData.child.age_group === age
                        ? "bg-orange-500 text-white"
                        : "border border-gray-300 text-gray-500 hover:border-gray-400"
                    }`}
                  >
                    {age === "4" ? "4세 이하" : `${age}세`}
                  </button>
                ))}
              </div>
            </div>
          </div> */}

          {/* 아이 개월수 */}
          {/* <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">아이 개월수</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="number"
                placeholder="생년월일을 입력하면 자동 계산됩니다."
                value={formData.child.birth_month}
                readOnly
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400 bg-gray-50"
              />
            </div>
          </div> */}

          {/* 아이 생년월일 */}
          <div className="mb-2">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">생년월일</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="date"
                value={formData.child.birth_date}
                onChange={(e) => handleChange("child", "birth_date", e.target.value)}
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base"
              />
            </div>
            {errors["child.birth_date"] && (
              <p className="text-red-500 text-sm mt-2">{errors["child.birth_date"]}</p>
            )}
          </div>

          {/* 자동 계산된 나이 정보 */}
          {childAgeInfo && (
            <div className="mb-6">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
                <label className="w-full md:w-32 flex-shrink-0" />
                <div className="flex-1 p-4 md:p-5 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-gray-700 mb-3">자동 계산된 나이:</p>
                  <div className="space-y-2">
                    <p className="text-base font-medium text-black">
                      한국 나이: <span className="text-orange-500 font-bold">{childAgeInfo.koreanAge}세</span>
                    </p>
                    <p className="text-base font-medium text-black">
                      만 나이: <span className="text-orange-500 font-bold">{childAgeInfo.actualAge}세</span>
                    </p>
                    <p className="text-base font-medium text-black">
                      개월: <span className="text-orange-500 font-bold">{childAgeInfo.birthMonth}개월</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <p className="text-gray-500 text-xs md:text-sm md:ml-44 mb-6">
            * 입력하신 생년월일은 맞춤 학습 제공과 생일 이벤트 안내에 활용됩니다.
          </p>
        </div>

        {/* 학부모 정보 입력 */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-lg md:text-xl font-medium text-orange-500 mb-4 md:mb-6">학부모 정보 입력</h2>

          {/* 이름 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">이름</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="text"
                placeholder="이름을 입력해 주세요."
                value={formData.parent.name}
                onChange={(e) => handleChange("parent", "name", e.target.value)}
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
              />
            </div>
            {errors["parent.name"] && (
              <p className="text-red-500 text-sm mt-2 md:ml-44">{errors["parent.name"]}</p>
            )}
          </div>

          {/* 성별 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0 md:pt-3.5">
                <span className="text-black text-base md:text-lg font-normal">성별</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <div className="flex gap-2 md:gap-3">
                {(["male", "female"] as Gender[]).map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    onClick={() => handleChange("parent", "gender", gender)}
                    className={`flex-1 px-12 md:px-24 py-3 md:py-3.5 rounded-lg text-sm md:text-base font-normal transition-colors ${
                      formData.parent.gender === gender
                        ? "bg-orange-500 text-white"
                        : "border border-gray-300 text-gray-500 hover:border-gray-400"
                    }`}
                  >
                    {gender === "male" ? "남성" : "여성"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 생년월일 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">생년월일</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="date"
                value={formData.parent.birth_date}
                onChange={(e) => handleChange("parent", "birth_date", e.target.value)}
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base"
              />
            </div>
          </div>

          {/* 아이디 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">아이디</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <div className="flex gap-2 md:gap-3 flex-col md:flex-row flex-1">
                <input
                  type="text"
                  placeholder="아이디를 입력해 주세요."
                  value={formData.parent.user_id}
                  onChange={(e) => handleChange("parent", "user_id", e.target.value)}
                  className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
                />
                <button
                  type="button"
                  className="px-6 md:px-8 py-3 md:py-3.5 bg-orange-500 text-white rounded-lg text-sm md:text-base font-medium hover:bg-orange-600 whitespace-nowrap"
                >
                  중복 확인
                </button>
              </div>
            </div>
            {errors["parent.user_id"] && (
              <p className="text-red-500 text-sm mt-2 md:ml-44">{errors["parent.user_id"]}</p>
            )}
          </div>

          {/* 비밀번호 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">비밀번호</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="password"
                placeholder="비밀번호를 입력해 주세요."
                value={formData.parent.password}
                onChange={(e) => handleChange("parent", "password", e.target.value)}
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
              />
            </div>
            {errors["parent.password"] && (
              <p className="text-red-500 text-sm mt-2 md:ml-44">{errors["parent.password"]}</p>
            )}
          </div>

          {/* 비밀번호 확인 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">비밀번호 확인</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="password"
                placeholder="비밀번호를 재입력해 주세요."
                value={formData.parent.password_confirm}
                onChange={(e) =>
                  handleChange("parent", "password_confirm", e.target.value)
                }
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
              />
            </div>
            {errors["parent.password_confirm"] && (
              <p className="text-red-500 text-sm mt-2 md:ml-44">
                {errors["parent.password_confirm"]}
              </p>
            )}
          </div>

          {/* 휴대전화 번호 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">휴대전화 번호</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <input
                type="tel"
                placeholder="-를 제외한 휴대전화번호를 입력해 주세요."
                value={formData.parent.phone_number}
                onChange={(e) => handleChange("parent", "phone_number", e.target.value)}
                className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
              />
            </div>
            {errors["parent.phone_number"] && (
              <p className="text-red-500 text-sm mt-2 md:ml-44">
                {errors["parent.phone_number"]}
              </p>
            )}
          </div>

          {/* 이메일 */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
              <label className="w-full md:w-32 flex-shrink-0">
                <span className="text-black text-base md:text-lg font-normal">이메일</span>
                <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
              </label>
              <div className="flex gap-2 md:gap-3 flex-col md:flex-row flex-1">
                <input
                  type="email"
                  placeholder="이메일 주소를 입력해 주세요."
                  value={formData.parent.email}
                  onChange={(e) => handleChange("parent", "email", e.target.value)}
                  className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
                />
                <button
                  type="button"
                  className={`px-6 md:px-8 py-3 md:py-3.5 rounded-lg text-sm md:text-base font-medium transition-colors whitespace-nowrap ${
                    formData.parent.is_email_verified
                      ? "bg-gray-200 text-gray-500"
                      : "bg-orange-500 text-white hover:bg-orange-600"
                  }`}
                  disabled={formData.parent.is_email_verified}
                >
                  인증하기
                </button>
              </div>
            </div>
            {errors["parent.email"] && (
              <p className="text-red-500 text-sm mt-2 md:ml-44">{errors["parent.email"]}</p>
            )}
          </div>

          {/* 인증 번호 */}
          {emailSent && (
            <div className="mb-6">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-12">
                <label className="w-full md:w-32 flex-shrink-0">
                  <span className="text-black text-base md:text-lg font-normal">인증 번호</span>
                  <span className="text-orange-500 text-base md:text-lg font-normal">*</span>
                </label>
                <div className="flex gap-2 md:gap-3 flex-col md:flex-row flex-1">
                  <input
                    type="text"
                    placeholder="이메일로 전송된 인증번호를 입력해 주세요."
                    value={formData.parent.email_verification_code}
                    onChange={(e) =>
                      handleChange("parent", "email_verification_code", e.target.value)
                    }
                    className="flex-1 h-12 md:h-14 px-4 md:px-5 py-3 md:py-3.5 rounded-lg border border-gray-300 text-sm md:text-base placeholder-gray-400"
                  />
                  <button
                    type="button"
                    className="px-6 md:px-8 py-3 md:py-3.5 bg-orange-500 text-white rounded-lg text-sm md:text-base font-medium hover:bg-orange-600 whitespace-nowrap"
                  >
                    확인
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 약관 동의 */}
        <div className="mb-8">
          <div className="flex gap-4 md:gap-8 mb-6 cursor-pointer" onClick={handleAllAgree}>
            <input
              type="checkbox"
              checked={formData.agree_to_all}
              onChange={handleAllAgree}
              className="w-6 md:w-7 h-6 md:h-7 flex-shrink-0 mt-0.5 md:mt-1"
            />
            <div>
              <p className="text-sm md:text-base font-medium text-black mb-1 md:mb-2">모두 동의합니다.</p>
              <p className="text-xs md:text-sm text-gray-400 leading-4 md:leading-5">
                이용약관, 개인정보 수집 및 활용(필수)/마케팅 활용(선택)에 대해 모두 동의 합니다.
                <br />
                전체동의는 필수 및 선택정보에 대한 동의도 포함되어 있으며, 개별적으로도
                동의를 선택하실 수 있습니다.
                <br />
                선택 항목은 동의를 거부하시는 경우에도 서비스의 이용은 가능하십니다.
              </p>
            </div>
          </div>

          <div className="p-4 md:p-7 border border-gray-300 rounded-lg space-y-3 md:space-y-4">
            {/* 법정대리인 동의 */}
            <div className="flex gap-4 md:gap-8">
              <input
                type="checkbox"
                checked={formData.agree_to_parental_consent}
                onChange={(e) =>
                  handleChange("agreements", "agree_to_parental_consent", e.target.checked)
                }
                className="w-6 md:w-7 h-6 md:h-7 flex-shrink-0 mt-0.5"
              />
              <div>
                <p className="text-sm md:text-base text-black">
                  상기 만 14세 미만 아동의 법정대리인으로서 위 사항에 대해 동의
                  <span className="text-orange-500">(필수)</span>
                </p>
              </div>
            </div>

            {/* 이용약관 */}
            <div className="flex gap-4 md:gap-8">
              <input
                type="checkbox"
                checked={formData.agree_to_terms}
                onChange={(e) =>
                  handleChange("agreements", "agree_to_terms", e.target.checked)
                }
                className="w-6 md:w-7 h-6 md:h-7 flex-shrink-0 mt-0.5"
              />
              <div className="flex gap-2 flex-1 flex-wrap">
                <p className="text-sm md:text-base text-black">
                  GGP 이용 약관 동의
                  <span className="text-orange-500">(필수)</span>
                </p>
                <button
                  type="button"
                  className="px-2 py-0.5 border border-gray-300 rounded-full text-xs text-gray-500 flex-shrink-0"
                >
                  전체보기
                </button>
              </div>
            </div>

            {/* 개인정보 수집 */}
            <div className="flex gap-4 md:gap-8">
              <input
                type="checkbox"
                checked={formData.agree_to_privacy}
                onChange={(e) =>
                  handleChange("agreements", "agree_to_privacy", e.target.checked)
                }
                className="w-6 md:w-7 h-6 md:h-7 flex-shrink-0 mt-0.5"
              />
              <div className="flex gap-2 flex-1 flex-wrap">
                <p className="text-sm md:text-base text-black">
                  개인 정보 수집 및 활용 동의
                  <span className="text-orange-500">(필수)</span>
                </p>
                <button
                  type="button"
                  className="px-2 py-0.5 border border-gray-300 rounded-full text-xs text-gray-500 flex-shrink-0"
                >
                  전체보기
                </button>
              </div>
            </div>

            {/* 마케팅 동의 */}
            <div className="flex gap-4 md:gap-8">
              <input
                type="checkbox"
                checked={formData.agree_to_marketing}
                onChange={(e) =>
                  handleChange("agreements", "agree_to_marketing", e.target.checked)
                }
                className="w-6 md:w-7 h-6 md:h-7 flex-shrink-0 mt-0.5"
              />
              <div className="flex gap-2 flex-1 flex-wrap">
                <p className="text-sm md:text-base text-black">
                  마케팅 활용 동의
                  <span className="text-gray-500">(선택)</span>
                </p>
                <button
                  type="button"
                  className="px-2 py-0.5 border border-gray-300 rounded-full text-xs text-gray-500 flex-shrink-0"
                >
                  전체보기
                </button>
              </div>
            </div>
          </div>
        </div>

        {errors["agree_to_terms"] && (
          <p className="text-red-500 text-sm mb-6">{errors["agree_to_terms"]}</p>
        )}
        {errors["agree_to_privacy"] && (
          <p className="text-red-500 text-sm mb-6">{errors["agree_to_privacy"]}</p>
        )}
        {errors["agree_to_parental_consent"] && (
          <p className="text-red-500 text-sm mb-6">{errors["agree_to_parental_consent"]}</p>
        )}
      </div>

      {/* 제출 버튼 */}
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={isLoading}
          className="w-full md:w-52 h-12 md:h-14 bg-orange-500 text-white rounded-full text-sm md:text-base font-medium hover:bg-orange-600 disabled:bg-gray-400 transition-colors"
        >
          {isLoading ? "처리 중..." : "무료체험 신청하기"}
        </button>
      </div>
    </form>
  );
}
